import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, CalendarDays } from "lucide-react";
import { BRAND, IMAGES } from "../data/site";
import ScheduleForm from "../components/ScheduleForm";

const Contact = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#schedule") {
      const el = document.getElementById("schedule");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      }
    }
  }, [hash]);

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="bg-stone-50 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-6">
            Contact
          </p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-stone-900 leading-[1.02] max-w-4xl">
            Let's talk about
            <span className="block italic text-[#9B4819]">your project.</span>
          </h1>
          <p className="mt-8 text-stone-600 text-base sm:text-lg max-w-2xl leading-relaxed">
            Pick up the phone — you'll reach a real person. We'll schedule a visit,
            walk the space, and give you a clear picture of what your remodel could
            look like.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Phone card — large */}
          <a
            href={BRAND.phoneLink}
            data-testid="contact-phone-card"
            className="group lg:col-span-7 relative overflow-hidden bg-stone-900 text-white p-10 md:p-16 min-h-[420px] flex flex-col justify-between transition-all duration-500 hover:-translate-y-1"
          >
            <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-[#9B4819] to-transparent" />
            <div className="relative flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#E8A976]" />
              <span className="uppercase text-xs tracking-[0.25em] text-stone-300">
                Call us
              </span>
            </div>
            <div className="relative">
              <div className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight group-hover:text-[#E8A976] transition-colors">
                {BRAND.phoneDisplay}
              </div>
              <div className="mt-6 flex items-center gap-2 text-stone-300 text-sm">
                Tap to call · straight to the owner
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
              </div>
              <div className="mt-4 uppercase text-[11px] tracking-[0.25em] text-stone-400">
                Licensed · Bonded · Insured · Oregon CCB #{BRAND.ccbLicense}
              </div>
            </div>
          </a>

          {/* Secondary contact info */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <a
              href={BRAND.emailLink}
              data-testid="contact-email-card"
              className="group bg-stone-50 border border-stone-200 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-stone-900"
            >
              <div className="flex items-center gap-3 mb-5">
                <Mail className="w-5 h-5 text-[#9B4819]" />
                <span className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold">
                  Email
                </span>
              </div>
              <div className="font-display text-xl sm:text-2xl text-stone-900 break-all">
                {BRAND.email}
              </div>
            </a>

            <div
              data-testid="contact-service-area-card"
              className="bg-stone-50 border border-stone-200 p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <MapPin className="w-5 h-5 text-[#9B4819]" />
                <span className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold">
                  Service area
                </span>
              </div>
              <div className="font-display text-xl sm:text-2xl text-stone-900">
                Oregon & Washington
              </div>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">
                Portland · Beaverton · Lake Oswego · Hillsboro · Vancouver ·
                Seattle · Bellevue and surrounding areas.
              </p>
            </div>

            <div
              data-testid="contact-hours-card"
              className="bg-stone-50 border border-stone-200 p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-5 h-5 text-[#9B4819]" />
                <span className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold">
                  Hours
                </span>
              </div>
              <ul className="text-stone-800 text-sm space-y-1.5">
                <li className="flex justify-between">
                  <span>Monday – Friday</span>
                  <span className="text-stone-500">7:00a – 6:00p</span>
                </li>
                <li className="flex justify-between">
                  <span>Saturday</span>
                  <span className="text-stone-500">Closed</span>
                </li>
                <li className="flex justify-between">
                  <span>Sunday</span>
                  <span className="text-stone-500">By appointment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule an appointment */}
      <section
        id="schedule"
        data-testid="schedule-section"
        className="bg-stone-50 py-24 md:py-32 scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[#9B4819] mb-5">
              <CalendarDays className="w-5 h-5" />
              <span className="uppercase text-xs tracking-[0.25em] font-semibold">
                Schedule an appointment
              </span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-stone-900 leading-[1.05]">
              Pick a day.
              <span className="block italic text-[#9B4819]">We'll call you.</span>
            </h2>
            <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed max-w-md">
              Tell us when works best and a bit about your project. We'll confirm
              by phone within one business day — no pressure, no obligation.
            </p>
            <ul className="mt-10 space-y-4 text-sm text-stone-700">
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#9B4819]/10 text-[#9B4819] text-xs font-semibold shrink-0 mt-0.5">1</span>
                Submit your preferred day and time window.
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#9B4819]/10 text-[#9B4819] text-xs font-semibold shrink-0 mt-0.5">2</span>
                We'll call to confirm and walk through the scope.
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#9B4819]/10 text-[#9B4819] text-xs font-semibold shrink-0 mt-0.5">3</span>
                On-site visit to measure and provide a detailed estimate.
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ScheduleForm />
          </div>
        </div>
      </section>

      {/* Banner image CTA */}
      <section className="relative overflow-hidden">
        <img
          src={IMAGES.living}
          alt="Modern living room remodel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/75" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-white text-center">
          <h2 className="font-display font-light text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Ready when you are.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#schedule"
              data-testid="contact-bottom-schedule-cta"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("schedule")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            >
              <CalendarDays className="w-5 h-5" />
              Schedule an appointment
            </a>
            <a
              href={BRAND.phoneLink}
              data-testid="contact-bottom-phone-cta"
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur-md text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              {BRAND.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
